import {
  AppointmentType,
  CreateAppointmentTypeRequest,
} from "@/app/types/AppointmentType";
import { del, get, patch, post, put, qs } from "./base";
import { Appointment, RawAppointment } from "@/app/types/Appointment";
import { BookReqBody } from "@/app/clinic/[clinicSlug]/types";
import { BlockTime, CreateBlockTimeRequest } from "@/app/types/BlockTime";
import { PatientsResponse } from "@/app/types/Patients";
import { ProviderRow } from "@/app/types/Provider";
import {
  CreateScheduleRequest,
  Schedule,
  UpdateScheduleRequest,
} from "@/app/types/Schedule";
import { SlotsRes } from "@/app/types/Slots";
import {
  Capability,
  Me,
  PendingInvite,
  StaffListResponse,
  StaffMember,
} from "@/app/types/Staff";
import { ClinicRow, UpdateClinicRequest } from "@/app/types/Clinic";
import { ClinicHours, ClinicHoursRequest } from "@/app/types/ClinicHours";
import {
  BillingCycle,
  BillingStatusResponse,
  ClinicInvoiceItem,
  InvoiceDetails,
} from "@/app/types/Billing";

export const adminRoutes = {
  appointmentTypes: {
    get: () =>
      get<{ items: AppointmentType[] }>(`/api/admin/appointment-types`),
    post: () =>
      post<AppointmentType, CreateAppointmentTypeRequest>(
        `/api/admin/appointment-types`
      ),
    patch: (id: string) =>
      patch<
        { success: boolean },
        Partial<{
          name: string;
          color: string;
          deleted: boolean;
          note: string | null;
        }>
      >(`/api/admin/appointment-types/${id}`),
    delete: (id: string) =>
      patch<AppointmentType, { deleted: boolean }>(
        `/api/admin/appointment-types/${id}`
      ),
  },
  appointments: {
    get: () => get<{ items: Appointment[] }>(`/api/admin/appointments`),
    getByProviderAndDate: (providerId: string, date: string) =>
      get<{ items: Appointment[] }>(
        `/api/admin/appointments?${qs({
          providerId,
          date,
        })}`
      ),
    post: () =>
      post<{ item: RawAppointment }, BookReqBody>(`/api/admin/appointments`),
    cancel: (id: string) =>
      patch<void, { status: string; notifyPatient: boolean }>(
        `/api/admin/appointments/${id}`
      ),
    updateNotes: (id: string) =>
      patch<void, { notes: string; mode: "notes" }>(
        `/api/admin/appointments/${id}`
      ),
    patch: (id: string) =>
      patch<{ item: RawAppointment }, Partial<BookReqBody>>(
        `/api/admin/appointments/${id}`
      ),
    getOne: (id: string) =>
      get<{ item: RawAppointment }>(`/api/admin/appointments/${id}`),
    getByRange: (
      providerId: string,
      rangeStart: string, // YYYY-MM-DD
      rangeEnd: string,
      typeId?: string,
      searchText?: string,
      showCancelled?: boolean
    ) =>
      get<{ items: Appointment[] }>(
        `/api/admin/appointments?${qs({
          providerId,
          rangeStart,
          rangeEnd,
          typeId,
          searchText,
          showCancelled: showCancelled ? "true" : undefined,
        })}`
      ),
    getByExactRange: (
      providerId: string,
      exactStart: string, // ISO datetime string
      exactEnd: string, // ISO datetime string
      typeId?: string,
      searchText?: string
    ) =>
      get<{ items: Appointment[] }>(
        `/api/admin/appointments?${qs({
          providerId,
          exactStart,
          exactEnd,
          typeId,
          searchText,
        })}`
      ),
  },
  blockTimes: {
    get: (providerId: string, currentAndFuture?: boolean) =>
      get<{ items: BlockTime[] }>(
        `/api/admin/block-times?${qs({
          providerId,
          currentAndFuture,
        })}`
      ),
    getByRange: (providerId: string, rangeStart: string, rangeEnd: string) =>
      get<{ items: BlockTime[] }>(
        `/api/admin/block-times?${qs({
          providerId,
          startDate: rangeStart,
          endDate: rangeEnd,
        })}`
      ),
    post: () =>
      post<BlockTime, CreateBlockTimeRequest>(`/api/admin/block-times`),
    put: (id: number) =>
      put<BlockTime, CreateBlockTimeRequest>(`/api/admin/block-times/${id}`),
    delete: (id: number) => del<void>(`/api/admin/block-times/${id}`),
  },
  me: {
    get: () => get<Me>(`/api/admin/me`),
  },
  staff: {
    get: () => get<StaffListResponse>(`/api/admin/staff`),
    invite: () =>
      post<
        { invite: PendingInvite; emailSent: boolean },
        {
          email: string;
          capabilities: Capability[];
          books_for_provider_ids: string[] | null;
          first_name?: string;
          last_name?: string;
          expertise_description?: string;
          title?: string;
        }
      >(`/api/admin/staff`),
    patch: (id: string) =>
      patch<
        { staff: StaffMember },
        Partial<{
          capabilities: Capability[];
          books_for_provider_ids: string[] | null;
          active: boolean;
        }>
      >(`/api/admin/staff/${id}`),
    remove: (id: string) => del<{ success: true }>(`/api/admin/staff/${id}`),
    revokeInvite: (id: string) =>
      del<{ success: true }>(`/api/admin/staff/invites/${id}`),
  },
  account: {
    deleteClinic: () =>
      post<{ success: true; purgeAfter: string }, undefined>(
        `/api/admin/account/delete-clinic`
      ),
    cancelDeleteClinic: () =>
      del<{ success: true }>(`/api/admin/account/delete-clinic`),
    leave: () => post<{ success: true }, undefined>(`/api/admin/account/leave`),
  },
  clinic: {
    get: () => get<{ clinic: ClinicRow }>(`/api/admin/clinic`),
    patch: () =>
      patch<{ clinic: ClinicRow }, UpdateClinicRequest>(`/api/admin/clinic`),
    uploadLogo: () => post<{ clinic: ClinicRow }, FormData>(`/api/admin/clinic/logo`),
    deleteLogo: () => del<{ clinic: ClinicRow }>(`/api/admin/clinic/logo`),
    uploadHeroPhoto: () => post<{ clinic: ClinicRow }, FormData>(`/api/admin/clinic/hero-photo`),
    deleteHeroPhoto: () => del<{ clinic: ClinicRow }>(`/api/admin/clinic/hero-photo`),
  },
  clinicHours: {
    get: () => get<{ hours: ClinicHours[] }>(`/api/admin/clinic-hours`),
    post: () =>
      post<{ hours: ClinicHours[] }, { hours: ClinicHoursRequest[] }>(
        `/api/admin/clinic-hours`
      ),
    patch: () =>
      patch<{ hours: ClinicHours[] }, { hours: ClinicHoursRequest[] }>(
        `/api/admin/clinic-hours`
      ),
  },
  // clinics: {
  //   get: () => get<{ items: { id: string; name: string }[] }>(`/api/admin/clinics`),
  // }
  freeClinics: {
    get: () => get<{ days: string[] }>(`/api/admin/free-clinics`),
  },
  freeDays: {
    get: (
      typeProviderId: string,
      providerId: string,
      month: string,
      ignoreAppointmentId?: string
    ) =>
      get<{ days: string[] }>(
        `/api/admin/free-days?${qs({
          typeProviderId,
          providerId,
          month,
          ignoreAppointmentId,
        })}`
      ),
  },
  patients: {
    get: (
      providerId: string,
      search: string,
      page: number,
      pageSize: number = 20
    ) =>
      get<PatientsResponse>(
        `/api/admin/patients?${qs({
          search,
          page,
          pageSize,
          providerId,
        })}`
      ),
  },
  providers: {
    // No create: providers exist only through registration or an accepted
    // invitation, because a schedule can only be edited by its own provider.
    get: () => get<{ items: ProviderRow[] }>(`/api/admin/providers`),
    patch: (id: string) =>
      patch<{ provider: ProviderRow }, Partial<ProviderRow>>(
        `/api/admin/providers/${id}`
      ),
    changePassword: (id: string) =>
      post<
        { success: true },
        { currentPassword: string; newPassword: string }
      >(
        `/api/admin/providers/${id}/change-password`
      ),
    changeEmail: (id: string) =>
      post<
        { success: true; provider: ProviderRow },
        { currentPassword: string; newEmail: string }
      >(`/api/admin/providers/${id}/change-email`),
    uploadPhoto: (id: string) =>
      post<{ provider: ProviderRow }, FormData>(
        `/api/admin/providers/${id}/photo`
      ),
    deletePhoto: (id: string) =>
      del<{ provider: ProviderRow }>(`/api/admin/providers/${id}/photo`),
  },
  schedules: {
    get: (providerId: string) =>
      get<{ items: Schedule[] }>(
        `/api/admin/schedules?providerId=${providerId}`
      ),
    post: () => post<Schedule, CreateScheduleRequest>(`/api/admin/schedules`),
    patch: (id: string) =>
      patch<Schedule, Partial<UpdateScheduleRequest>>(
        `/api/admin/schedules/${id}`
      ),
    delete: (id: string) =>
      patch<void, { deleted: boolean }>(`/api/admin/schedules/${id}`),
  },
  slots: {
    get: (
      typeProviderId: string,
      date: string,
      providerId?: string,
      ignoreAppointmentId?: string
    ) =>
      get<SlotsRes>(
        `/api/admin/slots?${qs({
          typeProviderId,
          date,
          providerId,
          ignoreAppointmentId,
        })}`
      ),
  },
  smsLogs: {
    getHistory: (months?: number) =>
      get<{ history: Array<{ month: string; count: number }> }>(
        `/api/admin/sms-logs/history${months ? `?months=${months}` : ""}`
      ),
  },
  billing: {
    status: () => get<BillingStatusResponse>(`/api/admin/billing/status`),
    checkout: () =>
      post<{ url: string }, undefined>(`/api/admin/billing/checkout`),
    plan: () =>
      post<
        { plan: string },
        { plan: "solo" | "plus"; cycle?: BillingCycle }
      >(`/api/admin/billing/plan`),
    cycle: () =>
      post<{ billingCycle: BillingCycle }, { cycle: BillingCycle }>(
        `/api/admin/billing/cycle`
      ),
    details: () =>
      post<{ success: boolean }, InvoiceDetails & { billingEmail: string }>(
        `/api/admin/billing/details`
      ),
    cancel: () =>
      post<{ cancelAt: string | null }, { cancel: boolean }>(
        `/api/admin/billing/cancel`
      ),
    invoices: () =>
      get<{ invoices: ClinicInvoiceItem[] }>(`/api/admin/billing/invoices`),
  },
};
